import axios from "axios";
import { DataTable } from "simple-datatables";
import VMasker from "vanilla-masker";
import { showAlert, showConfirm, handleRequestError } from "../../utils/alert";

/**
 * Validate CPF using official verification digit logic.
 *
 * @param {string} cpf
 * @returns {boolean}
 */
const isValidCPF = (cpf) => {
  const cleanCPF = cpf.replace(/\D/g, "");

  if (cleanCPF.length !== 11 || /^(\d)\1{10}$/.test(cleanCPF)) {
    return false;
  }

  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cleanCPF.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cleanCPF.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.substring(10, 11))) return false;

  return true;
};

/**
 * Validate CNPJ using official verification digit logic.
 *
 * @param {string} cnpj
 * @returns {boolean}
 */
const isValidCNPJ = (cnpj) => {
  const cleanCNPJ = cnpj.replace(/\D/g, "");

  if (cleanCNPJ.length !== 14 || /^(\d)\1{13}$/.test(cleanCNPJ)) {
    return false;
  }

  let size = cleanCNPJ.length - 2;
  let numbers = cleanCNPJ.substring(0, size);
  const digits = cleanCNPJ.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  size = size + 1;
  numbers = cleanCNPJ.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += numbers.charAt(size - i) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;

  return true;
};

export default () => ({
  // Modal States
  isModalOpen: false,
  isSubmitting: false,

  // Edit States
  editMode: false,
  editId: null,

  // Reset Password States
  isPasswordModalOpen: false,
  isResettingPassword: false,
  resetTenantId: null,
  resetTenantName: "",
  resetPasswordForm: {
    new_password: "",
  },

  // Data Storage
  tenantsList: [],
  plansList: [],

  // Finance Modal States
  isFinanceModalOpen: false,
  isLoadingFinance: false,
  activeFinanceTenant: null,
  financeData: null,
  gracePeriodDate: "",

  // KPI States
  kpis: {
    total_clients: 0,
    mrr: "0,00",
    realized_revenue: "0,00",
    overdue_count: 0,
    new_signups: 0,
  },

  // Form Payload
  tenantForm: {
    company_name: "",
    document: "",
    plan_slug: "",
    cycle: "MONTHLY",
    billing_type: "PIX",
    owner_name: "",
    owner_email: "",
    owner_password: "",
  },

  // DataTable Instance
  tenantsDataTable: null,

  async initTenants() {
    const token = localStorage.getItem("admin_jwt_token");
    if (!token) {
      window.location.href = "/admin/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    await this.fetchPlans();
    await this.loadTenantsTable();
  },

  // ================= API CALLS =================

  async fetchPlans() {
    try {
      const response = await axios.get("/api/admin/plans");
      this.plansList = response.data.data;
    } catch (error) {
      console.error("Falha ao carregar a lista de planos.", error);
    }
  },

  async loadTenantsTable() {
    try {
      const response = await axios.get("/api/admin/tenants");

      this.tenantsList = response.data.data;
      this.kpis = response.data.kpis;

      if (this.tenantsDataTable) {
        this.tenantsDataTable.destroy();
      }

      const tbody = document.querySelector("#tenantsTable tbody");
      tbody.innerHTML = this.tenantsList
        .map((tenant) => {
          let statusBadge = "";
          switch (tenant.status) {
            case "ACTIVE":
              statusBadge =
                '<span class="inline-flex rounded-md bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">Ativo</span>';
              break;
            case "TRIAL":
              statusBadge =
                '<span class="inline-flex rounded-md bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">Período de Teste</span>';
              break;
            case "OVERDUE":
              statusBadge =
                '<span class="inline-flex rounded-md bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">Inadimplente</span>';
              break;
            case "SUSPENDED":
              statusBadge =
                '<span class="inline-flex rounded-md bg-red-800 px-2 py-1 text-xs font-semibold text-white">Suspenso (Manual)</span>';
              break;
            case "CANCELLED":
              statusBadge =
                '<span class="inline-flex rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">Cancelado</span>';
              break;
            default:
              statusBadge = `<span class="inline-flex rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">${tenant.status}</span>`;
          }

          const billingText = tenant.billing_type
            ? tenant.billing_type.replace("_", " ").toLowerCase()
            : "";
          const isSuspended = tenant.status === "SUSPENDED";

          // ADICIONADO AQUI: classes de hover, even (zebrado) e transition no <tr>
          return `
            <tr class="hover:bg-slate-100 even:bg-slate-50 border-b border-gray-100 last:border-none transition-colors duration-200">
              <td class="py-4 px-4 xl:px-6 align-top">
                <p class="font-semibold text-slate-800">${tenant.name}</p>
                <p class="text-xs font-mono mt-0.5">
                  <a href="/agendar/${tenant.slug}" target="_blank" class="text-brand hover:underline font-medium">/agendar/${tenant.slug}</a>
                </p>
              </td>
              <td class="py-4 px-4 xl:px-6 text-sm text-slate-600 align-top">${tenant.plan_name}</td>
              <td class="py-4 px-4 xl:px-6 align-top">${statusBadge}</td>
              <td class="py-4 px-4 xl:px-6 align-top">
                <p class="text-sm font-medium text-slate-700">${tenant.expires_at}</p>
                <p class="text-xs text-slate-500 capitalize mt-0.5">${billingText}</p>
              </td>
              <td class="text-left py-4 px-4 xl:px-6 align-top w-65">
                <div class="grid grid-cols-2 gap-2 w-full min-w-60">

                  <!-- Impersonate Button -->
                  <button type="button" onclick="window.dispatchEvent(new CustomEvent('impersonate-tenant', {detail: '${tenant.id}'}))" class="w-full px-3 py-1.5 bg-slate-800/10 text-slate-800 hover:bg-slate-800 hover:text-white transition-colors text-xs font-bold rounded-lg flex items-center justify-start gap-1.5">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    Acessar
                  </button>

                  <!-- Finance Button -->
                  <button type="button" onclick="window.dispatchEvent(new CustomEvent('open-finance', {detail: {id: '${tenant.id}', name: '${tenant.name}'}}))" class="w-full px-3 py-1.5 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500 hover:text-white transition-colors text-xs font-bold rounded-lg flex items-center justify-start gap-1.5">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Financeiro
                  </button>

                  <!-- Toggle Suspension Button -->
                  <button type="button" onclick="window.dispatchEvent(new CustomEvent('toggle-suspension', {detail: '${tenant.id}'}))" class="w-full px-3 py-1.5 text-xs font-bold rounded-lg flex items-center justify-start gap-1.5 transition-colors ${isSuspended ? "bg-green-500/10 text-green-600 hover:bg-green-500 hover:text-white" : "bg-red-900/10 text-red-900 hover:bg-red-900 hover:text-white"}">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      ${
                        isSuspended
                          ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />'
                          : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />'
                      }
                    </svg>
                    ${isSuspended ? "Reativar" : "Bloquear"}
                  </button>

                  <!-- Edit Button -->
                  <button type="button" onclick="window.dispatchEvent(new CustomEvent('edit-tenant', {detail: '${tenant.id}'}))" class="w-full px-3 py-1.5 bg-blue-500/10 text-blue-600 hover:bg-blue-500 hover:text-white transition-colors text-xs font-bold rounded-lg flex items-center justify-start gap-1.5">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    Editar
                  </button>

                  <!-- Password & Delete Buttons -->
                  <button type="button" onclick="window.dispatchEvent(new CustomEvent('reset-password', {detail: {id: '${tenant.id}', name: '${tenant.name}'}}))" class="w-full px-3 py-1.5 bg-orange-500/10 text-orange-600 hover:bg-orange-500 hover:text-white transition-colors text-xs font-bold rounded-lg flex items-center justify-start gap-1.5">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                    Senha
                  </button>

                  <!-- Dynamic Action: Cancel / Reactivate -->
                  ${
                    tenant.status === "CANCELLED"
                      ? `
                      <button type="button" onclick="window.dispatchEvent(new CustomEvent('reactivate-tenant', {detail: '${tenant.id}'}))" class="w-full px-3 py-1.5 bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500 hover:text-white transition-colors text-xs font-bold rounded-lg flex items-center justify-start gap-1.5">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        Reativar
                      </button>
                    `
                      : `
                      <button type="button" onclick="window.dispatchEvent(new CustomEvent('delete-tenant', {detail: '${tenant.id}'}))" class="w-full px-3 py-1.5 bg-red-500/10 text-red-600 hover:bg-red-500 hover:text-white transition-colors text-xs font-bold rounded-lg flex items-center justify-start gap-1.5">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                        Cancelar
                      </button>
                    `
                  }
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.tenantsDataTable = new DataTable("#tenantsTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} estabelecimentos",
          noRows: "Nenhum estabelecimento registado",
          noResults: "Nenhum resultado encontrado",
          searchTitle: "Pesquisar...",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  // ================= SUPPORT / IMPERSONATION =================

  async impersonateTenant(id) {
    try {
      const response = await axios.post(`/api/admin/tenants/${id}/impersonate`);
      const data = response.data;

      const clientUrl = `/client/dashboard?auth_code=${data.auth_code}`;

      window.open(clientUrl, "_blank");
      showAlert("Sessão de suporte iniciada na nova aba.", "success");
    } catch (error) {
      handleRequestError(error, "Falha ao iniciar sessão no cliente.");
    }
  },

  async toggleSuspension(id) {
    const result = await showConfirm(
      "Alterar Status de Acesso",
      "Deseja alternar o status de suspensão deste estabelecimento? Isso bloqueará/desbloqueará o acesso imediatamente.",
    );

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `/api/admin/tenants/${id}/toggle-suspension`,
        );
        showAlert(response.data.message, "success");
        await this.loadTenantsTable();
      } catch (error) {
        handleRequestError(error, "Erro ao alterar suspensão.");
      }
    }
  },

  // ================= FORM UTILS & LOGIC =================

  handleDocumentInput(event) {
    const rawValue = event.target.value;

    if (!rawValue) {
      this.tenantForm.document = "";
      return;
    }

    let onlyNumbers = rawValue.replace(/\D/g, "");

    if (onlyNumbers.length <= 11) {
      this.tenantForm.document = VMasker.toPattern(
        onlyNumbers,
        "999.999.999-99",
      );
    } else {
      onlyNumbers = onlyNumbers.substring(0, 14);
      this.tenantForm.document = VMasker.toPattern(
        onlyNumbers,
        "99.999.999/9999-99",
      );
    }
  },

  getSelectedPlanPrice() {
    if (!this.tenantForm.plan_slug) return "R$ 0,00";

    const plan = this.plansList.find(
      (p) => p.slug === this.tenantForm.plan_slug,
    );
    if (!plan) return "R$ 0,00";

    let rawPrice = 0;

    if (plan.prices && plan.prices.length > 0) {
      const priceObj = plan.prices.find(
        (p) => p.cycle === this.tenantForm.cycle,
      );
      if (priceObj) {
        rawPrice = parseFloat(priceObj.price);
      }
    } else {
      const basePrice = parseFloat(plan.price || 0);
      if (this.tenantForm.cycle === "MONTHLY") rawPrice = basePrice;
      if (this.tenantForm.cycle === "SEMIANNUALLY") rawPrice = basePrice * 6;
      if (this.tenantForm.cycle === "YEARLY") rawPrice = basePrice * 12;
    }

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(rawPrice);
  },

  // ================= MODAL HANDLERS =================

  openCreateTenant() {
    this.editMode = false;
    this.tenantForm = {
      company_name: "",
      document: "",
      plan_slug: "",
      cycle: "MONTHLY",
      billing_type: "PIX",
      owner_name: "",
      owner_email: "",
      owner_password: "",
    };
    this.isModalOpen = true;
  },

  openEditTenant(id) {
    this.editMode = true;
    this.editId = id;
    const tenant = this.tenantsList.find((t) => t.id === id);

    this.tenantForm = {
      company_name: tenant.name,
      document: "",
      plan_slug: "",
      cycle: "MONTHLY",
      billing_type: "PIX",
      owner_name: "",
      owner_email: "",
      owner_password: "",
    };
    this.isModalOpen = true;
  },

  // ================= SUBMIT =================

  async submitTenant() {
    if (!this.editMode) {
      const rawDocument = this.tenantForm.document.replace(/\D/g, "");

      if (rawDocument.length <= 11 && !isValidCPF(rawDocument)) {
        showAlert("O CPF informado é inválido.", "error");
        return;
      } else if (rawDocument.length > 11 && !isValidCNPJ(rawDocument)) {
        showAlert("O CNPJ informado é inválido.", "error");
        return;
      }
    }

    this.isSubmitting = true;

    try {
      if (this.editMode) {
        const payload = {
          name: this.tenantForm.company_name,
        };
        await axios.put(`/api/admin/tenants/${this.editId}`, payload);
        showAlert("Cadastro atualizado com sucesso!", "success");
      } else {
        const payload = {
          ...this.tenantForm,
          document: this.tenantForm.document.replace(/\D/g, ""),
        };

        await axios.post("/api/admin/tenants", payload);
        showAlert("Estabelecimento integrado ao Asaas com sucesso!", "success");
      }

      await this.loadTenantsTable();
      this.isModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao guardar o estabelecimento.");
    } finally {
      this.isSubmitting = false;
    }
  },

  async deleteTenant(id) {
    const result = await showConfirm(
      "Cancelar Assinatura",
      "ATENÇÃO: Deseja cancelar a assinatura deste estabelecimento? O acesso ao sistema será bloqueado e as cobranças no Asaas serão interrompidas, mas os dados históricos serão mantidos.",
    );

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/admin/tenants/${id}`);
        await this.loadTenantsTable();
        showAlert(
          "Assinatura cancelada e acesso bloqueado com sucesso.",
          "success",
        );
      } catch (error) {
        handleRequestError(error, "Erro ao cancelar a assinatura do cliente.");
      }
    }
  },

  async reactivateTenant(id) {
    const result = await showConfirm(
      "Reativar Assinatura",
      "Deseja reativar a assinatura deste estabelecimento? O sistema tentará gerar um novo contrato de cobrança no Asaas mantendo os valores originais.",
    );

    if (result.isConfirmed) {
      try {
        await axios.post(`/api/admin/tenants/${id}/reactivate`);
        await this.loadTenantsTable();
        showAlert(
          "Assinatura reativada e acesso restaurado com sucesso.",
          "success",
        );
      } catch (error) {
        handleRequestError(
          error,
          "Erro ao tentar reativar a assinatura do cliente.",
        );
      }
    }
  },

  // ================= PASSWORD RESET MODAL =================
  openResetPasswordModal(data) {
    this.resetTenantId = data.id;
    this.resetTenantName = data.name;
    this.resetPasswordForm.new_password = "";
    this.isPasswordModalOpen = true;
  },

  async submitPasswordReset() {
    this.isResettingPassword = true;
    try {
      await axios.put(
        `/api/admin/tenants/${this.resetTenantId}/reset-password`,
        this.resetPasswordForm,
      );
      showAlert(
        `A senha do estabelecimento "${this.resetTenantName}" foi atualizada com sucesso!`,
        "success",
      );
      this.isPasswordModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao redefinir a senha.");
    } finally {
      this.isResettingPassword = false;
    }
  },

  // ================= FINANCE MODULE =================

  async openFinanceModal(data) {
    this.activeFinanceTenant = data;
    this.financeData = null;
    this.gracePeriodDate = "";
    this.isFinanceModalOpen = true;
    this.isLoadingFinance = true;

    try {
      const response = await axios.get(`/api/admin/tenants/${data.id}/finance`);
      this.financeData = response.data;
    } catch (error) {
      handleRequestError(
        error,
        "Erro ao carregar dados financeiros do gateway.",
      );
      this.isFinanceModalOpen = false;
    } finally {
      this.isLoadingFinance = false;
    }
  },

  async applyGracePeriod() {
    if (!this.gracePeriodDate || !this.activeFinanceTenant) return;

    try {
      const response = await axios.patch(
        `/api/admin/tenants/${this.activeFinanceTenant.id}/grace-period`,
        {
          expires_at: this.gracePeriodDate,
        },
      );

      showAlert(response.data.message, "success");
      await this.loadTenantsTable();
      this.isFinanceModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao aplicar período de carência.");
    }
  },

  async syncTenantSubscription() {
    if (!this.activeFinanceTenant) return;

    this.isLoadingFinance = true;
    try {
      const response = await axios.post(
        `/api/admin/tenants/${this.activeFinanceTenant.id}/sync`,
      );
      showAlert(
        response.data.message,
        response.data.status === "ACTIVE" ? "success" : "info",
      );

      await this.loadTenantsTable();
      await this.openFinanceModal(this.activeFinanceTenant);
    } catch (error) {
      handleRequestError(error, "Erro ao tentar sincronizar a assinatura.");
      this.isLoadingFinance = false;
    }
  },

  copyInvoiceUrl() {
    if (
      this.financeData &&
      this.financeData.pending_payment &&
      this.financeData.pending_payment.invoice_url
    ) {
      navigator.clipboard.writeText(
        this.financeData.pending_payment.invoice_url,
      );
      showAlert(
        "Link da fatura copiado para a área de transferência!",
        "success",
      );
    }
  },

  formatCurrency(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  },

  formatDate(dateString) {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  },
});
